import * as React from 'react'
import { useMemo } from 'react'

import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import useTheme from '@mui/material/styles/useTheme'
import Stack from '@mui/material/Stack'

import { ConditionActionsButton } from './ConditionActionsButton'
import type { ConditionModel } from '../../../typings'
import { useTranslation } from 'react-i18next'

type Props = {
  data: ConditionModel
  idx: number
  removeCondition: (idx: number) => void
  editCondition: (data: ConditionModel, index: number) => void
}

export function ConditionItem(props: Props) {
  const {
    data: { assignmentRuleConditionValues, matchType, attribute, id },
    idx,
    removeCondition,
    editCondition,
  } = props
  const theme = useTheme()
  const { t } = useTranslation()

  const values = useMemo(() => {
    const names = assignmentRuleConditionValues.map(
      (value: any) =>
        `${
          value?.name ||
          value?.label ||
          value?.fullName ||
          value?.value ||
          value
        }${value?.abbreviation ? ` (${value?.abbreviation})` : ''}${
          value?.description ? ` - ${value?.description}` : ''
        }`,
    )
    return ` ${names.join(', ')}`
  }, [assignmentRuleConditionValues])

  const title = useMemo(() => {
    switch (attribute) {
      case 'ZipCode':
        return t('postalCode')
      case 'Branch':
        return 'Branch'
      case 'Campaign':
        return 'Campaign'
      case 'LeadSource':
        return 'Lead Source'
      case 'State':
        return t('stateAssignmentRuleLabel')
      case 'LocationType':
        return 'Location Type'
      case 'Division':
        return 'Division'
      case 'CreatedBy':
        return 'Created By'
      case 'TargetPest':
        return 'Target Pests'
      case 'LeadType':
        return 'Type'
      case 'Territory':
        return 'Territory'
      default:
        return ''
    }
  }, [attribute, t])

  return (
    <Paper variant="elevation" elevation={2}>
      <Stack
        paddingX={2}
        paddingY={1.2}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap={2}
      >
        <Stack>
          <Typography variant="subtitle1" color={theme.palette.text.primary}>
            {title}
          </Typography>
          <Stack direction="row" alignItems="center">
            <Typography
              variant="body2"
              color={theme.palette.text.primary}
              letterSpacing={0.17}
              fontFamily="Source Sans Pro Semibold"
              paddingRight="5px"
            >
              {matchType}: &nbsp;
            </Typography>
            <Typography
              variant="body2"
              color={theme.palette.text.primary}
              letterSpacing={0.17}
            >
              {values}
            </Typography>
          </Stack>
        </Stack>
        <ConditionActionsButton
          dataTestId={`conditionMoreBtn-${idx}`}
          remove={() => removeCondition(idx)}
          edit={() =>
            editCondition(
              {
                id,
                assignmentRuleConditionValues,
                attribute,
                matchType,
              },
              idx,
            )
          }
        />
      </Stack>
    </Paper>
  )
}
